import { useState } from "react";
import { Link } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { useLang } from "@/i18n/LangContext";
import { api } from "@/lib/api";
import Spinner from "@/components/Spinner";
import AdBanner from "@/components/AdBanner";
import type { EpicGame, SteamFreeGame } from "@/types/api";

interface FreeGamesData {
  epic: { freeNow: EpicGame[]; upcoming: EpicGame[]; current?: EpicGame[] };
  steam: SteamFreeGame[];
}

function getRemainingDays(endDate?: string | number | null): number | null {
  if (!endDate) return null;
  const end = typeof endDate === "number" ? endDate * 1000 : new Date(endDate).getTime();
  const diff = end - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getEpicImage(game: EpicGame): string {
  const wide = game.keyImages?.find((k) => k.type === "OfferImageWide" || k.type === "DieselStoreFrontWide");
  const tall = game.keyImages?.find((k) => k.type === "Thumbnail" || k.type === "OfferImageTall");
  return wide?.url || tall?.url || game.keyImages?.[0]?.url || "";
}

export default function FreeGamesPage() {
  const { t } = useLang();
  const [deadlineFilter, setDeadlineFilter] = useState(0);

  const { data, loading, error } = useFetch<FreeGamesData>(() => api.freeGames("ko") as Promise<FreeGamesData>, []);

  if (loading) return <Spinner />;
  if (error || !data) return <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-12 text-center text-sm text-gray-400 dark:text-sub-text">무료 게임 정보를 불러올 수 없어요.</div>;

  const epicFreeNow = data.epic.freeNow || data.epic.current || [];
  const epicUpcoming = data.epic.upcoming || [];
  const steamFree = data.steam || [];

  const formatRemaining = (endDate?: string | number | null): string | null => {
    const days = getRemainingDays(endDate);
    if (days === null) return null;
    if (days <= 0) return t.common?.endsToday || "곧 종료";
    if (days === 1) return t.common?.endsTomorrow || "내일 종료";
    return `${days}${t.common?.daysLeft || "일 남음"}`;
  };

  const DEADLINE_FILTERS = [
    { value: 0, label: t.common?.all || "전체" },
    { value: 1, label: t.freeGames?.within1Day || "1일 이내" },
    { value: 3, label: t.freeGames?.within3Days || "3일 이내" },
    { value: 7, label: t.freeGames?.within7Days || "7일 이내" },
  ];

  const filteredEpic = deadlineFilter === 0 ? epicFreeNow : epicFreeNow.filter((g) => { const d = getRemainingDays(g.promoEndDate); return d !== null && d <= deadlineFilter; });
  const filteredSteam = deadlineFilter === 0 ? steamFree : steamFree.filter((g) => { const d = getRemainingDays(g.discount_expiration); return d !== null && d <= deadlineFilter; });
  const totalFree = filteredEpic.length + filteredSteam.length;
  const totalAll = epicFreeNow.length + steamFree.length;

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 flex-1 w-full">
      {/* Description card — 원본과 동일 */}
      <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4 sm:p-5 mt-6 mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
          </div>
          <h1 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">{t.freeGames?.title || "무료 게임"}</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-muted leading-relaxed">{t.freeGames?.description || "에픽 주간 무료 게임과 스팀 무료 게임을 확인할 수 있어요."}</p>
      </div>

      {/* Stats banner */}
      {totalAll > 0 && (
        <div className="pt-6 pb-2">
          <div className="text-center mb-4">
            <p className="text-sm font-medium text-gray-500 dark:text-muted mb-1">{t.freeGames?.currentFree || "현재 무료"}</p>
            <p className="text-3xl sm:text-5xl font-black text-gray-900 dark:text-white tabular-nums">
              {totalFree}
              <span className="text-sm sm:text-lg font-medium text-gray-400 dark:text-muted ml-1.5">{t.common?.games || "개"}</span>
            </p>
          </div>
          <div className="flex items-center justify-center gap-2 mb-6">
            {DEADLINE_FILTERS.map((f) => (
              <button key={f.value} onClick={() => setDeadlineFilter(f.value)} className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${deadlineFilter === f.value ? "bg-gray-900 text-white shadow-sm" : "bg-card-bg text-gray-500 dark:text-muted border border-gray-200 dark:border-card-border hover:bg-inner-bg dark:hover:bg-hover-bg hover:text-gray-900 dark:hover:text-white"}`}>{f.label}</button>
            ))}
          </div>
        </div>
      )}

      {/* Results count */}
      {totalAll > 0 && (
        <div className="mb-3 px-1">
          <p className="text-sm text-gray-500 dark:text-muted">
            {deadlineFilter > 0 ? (
              <><span className="text-gray-900 dark:text-white font-semibold">{deadlineFilter}{t.freeGames?.withinDays || "일 이내"}</span> {t.freeGames?.deadline || "마감"} <span className="text-gray-900 dark:text-white font-semibold">{totalFree}</span>{t.common?.games || "개"}</>
            ) : (
              <>{t.common?.total || "총"} <span className="text-gray-900 dark:text-white font-semibold">{totalAll}</span>{t.freeGames?.totalFreeGames || "개 무료 게임"}</>
            )}
          </p>
        </div>
      )}

      {/* Epic Free Now */}
      {filteredEpic.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center gap-3 mb-4 px-1">
            <div className="w-7 h-7 rounded-lg bg-gray-900 flex items-center justify-center"><span className="text-white text-[10px] font-black">E</span></div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">{t.freeGames?.epicGames || "Epic Games"}</h2>
            <span className="text-[11px] font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-md">{t.freeGames?.freeNow || "무료"}</span>
            <span className="text-xs text-gray-400 dark:text-muted ml-auto">{filteredEpic.length}{t.common?.games || "개"}</span>
          </div>
          <div className="flex flex-col gap-2.5">
            {filteredEpic.map((game) => {
              const remaining = formatRemaining(game.promoEndDate);
              const days = getRemainingDays(game.promoEndDate);
              const urgent = days !== null && days <= 2;
              const cardClass = "group bg-card-bg rounded-xl border border-gray-200 dark:border-card-border overflow-hidden hover:shadow-md hover:border-gray-300 dark:hover:border-hover-active transition-all duration-200";
              const cardInner = (
                <div className="flex items-center">
                  <div className="relative shrink-0 w-[100px] h-[60px] sm:w-[220px] sm:h-[100px] bg-hover-bg dark:bg-background overflow-hidden">
                    <img src={getEpicImage(game)} alt={game.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0 px-2.5 sm:px-5 py-2 sm:py-3 flex items-center gap-2 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-xs sm:text-[15px] text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors line-clamp-1">{game.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-muted mt-1 line-clamp-1 hidden sm:block">{game.description}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[11px] text-gray-400 dark:text-muted bg-inner-bg px-2 py-0.5 rounded-md border border-inner-border dark:border-card-border font-medium">Epic Games</span>
                        {remaining && (
                          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${urgent ? "bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30 animate-pulse" : "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30"}`}>{remaining}</span>
                        )}
                      </div>
                    </div>
                    <span className="bg-emerald-500 text-white text-sm font-bold px-3 py-1.5 rounded-lg shrink-0">{t.common?.free || "무료"}</span>
                  </div>
                </div>
              );
              return (
                <Link key={game.id} to={`/free-games/${game.id}`} className={cardClass}>{cardInner}</Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Steam 100% off */}
      {filteredSteam.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center gap-3 mb-4 px-1">
            <div className="w-7 h-7 rounded-lg bg-[#1b2838] flex items-center justify-center"><span className="text-white text-[10px] font-black">S</span></div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Steam</h2>
            <span className="text-[11px] font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-md">{t.freeGames?.steamFree || "100% 할인"}</span>
            <span className="text-xs text-gray-400 dark:text-muted ml-auto">{filteredSteam.length}{t.common?.games || "개"}</span>
          </div>
          <div className="flex flex-col gap-2.5">
            {filteredSteam.map((game) => {
              const remaining = formatRemaining(game.discount_expiration);
              const days = getRemainingDays(game.discount_expiration);
              const urgent = days !== null && days <= 2;
              return (
                <Link key={game.appid} to={`/game/${game.appid}`} className="group bg-card-bg rounded-xl border border-gray-200 dark:border-card-border overflow-hidden hover:shadow-md hover:border-gray-300 dark:hover:border-hover-active transition-all duration-200">
                  <div className="flex items-center">
                    <div className="relative shrink-0 w-[100px] h-[60px] sm:w-[220px] sm:h-[100px] bg-hover-bg dark:bg-background overflow-hidden">
                      <img src={game.header_image} alt={game.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0 px-2.5 sm:px-5 py-2 sm:py-3 flex items-center gap-2 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-xs sm:text-[15px] text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors line-clamp-1">{game.name}</h3>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[11px] text-gray-400 dark:text-muted bg-inner-bg px-2 py-0.5 rounded-md border border-inner-border dark:border-card-border font-medium">Steam</span>
                          {remaining && (
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${urgent ? "bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30 animate-pulse" : "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30"}`}>{remaining}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 shrink-0">
                        <span className="bg-emerald-500 text-white text-sm font-bold px-2 py-1 rounded-lg tabular-nums">-100%</span>
                        <span className="text-[15px] font-bold text-gray-900 dark:text-white leading-tight">{t.common?.free || "무료"}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Epic Upcoming */}
      {epicUpcoming.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center gap-3 mb-4 px-1">
            <div className="w-7 h-7 rounded-lg bg-gray-400 flex items-center justify-center"><span className="text-white text-[10px] font-black">E</span></div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">{t.freeGames?.nextWeekFree || "다음 주 무료"}</h2>
            <span className="text-[11px] font-bold bg-blue-500 text-white px-2 py-0.5 rounded-md">{t.freeGames?.scheduled || "예정"}</span>
            <span className="text-xs text-gray-400 dark:text-muted ml-auto">{epicUpcoming.length}{t.common?.games || "개"}</span>
          </div>
          <div className="flex flex-col gap-2.5">
            {epicUpcoming.map((game) => {
              const upClass = "group bg-card-bg rounded-xl border border-gray-200 dark:border-card-border overflow-hidden hover:shadow-md hover:border-gray-300 dark:hover:border-hover-active transition-all duration-200";
              const upInner = (
                <div className="flex items-center">
                  <div className="relative shrink-0 w-[100px] h-[60px] sm:w-[220px] sm:h-[100px] bg-hover-bg dark:bg-background overflow-hidden">
                    <img src={getEpicImage(game)} alt={game.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 opacity-80 group-hover:opacity-100" loading="lazy" />
                    <div className="absolute inset-0 bg-gray-900/10" />
                  </div>
                  <div className="flex-1 min-w-0 px-2.5 sm:px-5 py-2 sm:py-3 flex items-center gap-2 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-xs sm:text-[15px] text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors line-clamp-1">{game.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-muted mt-1 line-clamp-1 hidden sm:block">{game.description}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[11px] text-gray-400 dark:text-muted bg-inner-bg px-2 py-0.5 rounded-md border border-inner-border dark:border-card-border font-medium">Epic Games</span>
                      </div>
                    </div>
                    <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shrink-0">{t.freeGames?.nextWeek || "다음 주"}</span>
                  </div>
                </div>
              );
              return (
                <Link key={game.id} to={`/free-games/${game.id}`} className={upClass}>{upInner}</Link>
              );
            })}
          </div>
        </section>
      )}

      {/* No results */}
      {filteredEpic.length === 0 && epicUpcoming.length === 0 && filteredSteam.length === 0 && (
        <div className="flex flex-col items-center justify-center py-28 gap-2">
          <p className="text-gray-400 dark:text-muted text-sm">{deadlineFilter > 0 ? (t.freeGames?.noFreeGamesWithinDays || "해당 기간 내 무료 게임이 없어요") : (t.freeGames?.noFreeGamesFound || "현재 무료 게임이 없어요")}</p>
          {deadlineFilter > 0 && <button onClick={() => setDeadlineFilter(0)} className="text-sm text-blue-500 hover:text-blue-600 font-medium mt-1">{t.common?.viewAll || "전체 보기"}</button>}
        </div>
      )}

      <AdBanner className="my-4" />
    </div>
  );
}
